import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Eye, Loader2, BarChart2 } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from '../components/ui/use-toast';
import {
  FormField,
  FormPage,
  flattenPages,
  pagesFromForm,
} from './preChatFormStructure';

interface PreChatForm {
  id: number;
  title: string;
  subtitle?: string;
  fields?: FormField[];
  pages?: FormPage[];
  total_submissions: number;
}

interface FormSubmission {
  id: number;
  form_data: Record<string, any>;
  initial_message?: string;
  submitted_at: string;
  contact_id?: number;
  conversation_id?: number;
  surface?: string;
}

function formatAnswer(value: any): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function previewAnswers(data: Record<string, any>, max = 3): string {
  const entries = Object.entries(data || {}).filter(
    ([k, v]) => k !== 'name' && v !== null && v !== undefined && v !== '',
  );
  if (!entries.length) return '—';
  return entries
    .slice(0, max)
    .map(([k, v]) => `${k}: ${formatAnswer(v)}`)
    .join(' · ');
}

function downloadCsv(filename: string, rows: string[][]) {
  const escape = (cell: string) => {
    if (/[",\n]/.test(cell)) return `"${cell.replace(/"/g, '""')}"`;
    return cell;
  };
  const body = rows.map((r) => r.map((c) => escape(String(c ?? ''))).join(',')).join('\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const PreChatFormResponsesPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const id = Number(formId);

  const [form, setForm] = useState<PreChatForm | null>(null);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<FormSubmission | null>(null);

  const fieldLabels = useMemo(() => {
    if (!form) return new Map<string, string>();
    const pages = pagesFromForm(form);
    const map = new Map<string, string>();
    for (const f of flattenPages(pages)) {
      map.set(f.id, f.label || f.id);
    }
    return map;
  }, [form]);

  const load = async () => {
    if (!Number.isFinite(id) || id <= 0) {
      toast({ title: 'Invalid form', variant: 'destructive' });
      navigate('/dashboard/pre-chat-forms');
      return;
    }
    try {
      setLoading(true);
      const [formRes, subsRes] = await Promise.all([
        api.get<PreChatForm>(`/pre-chat-forms/${id}`),
        api.get<FormSubmission[]>(`/pre-chat-forms/${id}/submissions`, {
          params: { page: 1, per_page: 200 },
        }),
      ]);
      setForm(formRes.data);
      const raw = subsRes.data as any;
      setSubmissions(Array.isArray(raw) ? raw : raw?.submissions || []);
    } catch {
      toast({ title: 'Failed to load responses', variant: 'destructive' });
      setForm(null);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleExportCsv = () => {
    if (!submissions.length) return;
    const allKeys = new Set<string>();
    submissions.forEach((s) => Object.keys(s.form_data || {}).forEach((k) => allKeys.add(k)));
    const keys = Array.from(allKeys);
    const header = [
      'Submitted',
      'Surface',
      'Contact',
      ...keys.map((k) => fieldLabels.get(k) || k),
      'Initial message',
    ];
    const rows = submissions.map((s) => [
      s.submitted_at ? new Date(s.submitted_at).toLocaleString() : '',
      s.surface || 'widget',
      s.form_data?.name || s.form_data?.email || '',
      ...keys.map((k) => formatAnswer(s.form_data?.[k])),
      s.initial_message || '',
    ]);
    downloadCsv(`pre_chat_form_${id}_responses.csv`, [header, ...rows]);
    toast({ title: 'CSV downloaded' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading responses…
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 py-4 px-4 md:py-6 lg:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="mb-1 -ml-2 gap-1.5 text-muted-foreground"
            onClick={() => navigate('/dashboard/pre-chat-forms')}
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Pre-Chat Forms
          </Button>
          <h1 className="text-xl font-semibold tracking-tight">
            {form?.title || `Form #${id}`}
          </h1>
          <p className="text-sm text-muted-foreground">
            {submissions.length} response{submissions.length !== 1 ? 's' : ''}
            {form ? ` · ${form.total_submissions ?? 0} total submissions` : ''}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          disabled={!submissions.length}
          onClick={handleExportCsv}
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </Button>
      </div>

      {submissions.length === 0 ? (
        <Card className="shadow-sm">
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted mb-4">
              <BarChart2 className="h-8 w-8 text-muted-foreground/60" />
            </div>
            <h3 className="font-semibold mb-1">No responses yet</h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              Publish the form and share its web link or WhatsApp Flow to start collecting answers.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="shadow-sm overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Responses</CardTitle>
            <CardDescription className="text-xs">
              Newest first · click View for full answers
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Surface</TableHead>
                  <TableHead className="text-xs">Contact / Name</TableHead>
                  <TableHead className="text-xs">Answers</TableHead>
                  <TableHead className="text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {sub.submitted_at
                        ? new Date(sub.submitted_at).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px] capitalize">
                        {sub.surface || 'widget'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {sub.form_data?.name ||
                        sub.form_data?.email ||
                        (sub.contact_id ? `Contact #${sub.contact_id}` : (
                          <span className="text-muted-foreground font-normal">Anonymous</span>
                        ))}
                    </TableCell>
                    <TableCell className="max-w-[280px]">
                      <p className="text-xs text-muted-foreground truncate">
                        {previewAnswers(sub.form_data || {})}
                      </p>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        onClick={() => {
                          setDetail(sub);
                          setDetailOpen(true);
                        }}
                      >
                        <Eye className="h-3 w-3" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 py-3.5 pr-14 text-left">
            <DialogTitle>Response detail</DialogTitle>
            <DialogDescription>
              {detail?.submitted_at
                ? new Date(detail.submitted_at).toLocaleString()
                : 'Submission'}
              {detail?.surface ? ` · ${detail.surface}` : ''}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="px-6 py-4 space-y-3">
              {detail &&
                Object.entries(detail.form_data || {}).map(([key, value]) => (
                  <div key={key} className="space-y-0.5">
                    <p className="text-xs font-medium text-muted-foreground">
                      {fieldLabels.get(key) || key}
                    </p>
                    <p className="text-sm">{formatAnswer(value)}</p>
                  </div>
                ))}
              {detail?.initial_message && (
                <div className="space-y-0.5 pt-2 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground">Initial message</p>
                  <p className="text-sm">{detail.initial_message}</p>
                </div>
              )}
              {detail && !Object.keys(detail.form_data || {}).length && !detail.initial_message && (
                <p className="text-sm text-muted-foreground">No answers recorded.</p>
              )}
            </div>
          </ScrollArea>
          <DialogFooter className="shrink-0 border-t border-border px-6 py-3.5">
            <Button variant="outline" onClick={() => setDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreChatFormResponsesPage;
